import { useIDESelectors } from '@/hooks/use-ide-selector';
import { useIDEState } from '@/hooks/use-ide-state';
import React, { memo, useCallback } from 'react';
import { ProjectItem } from './project-item';

export const FileExplorer: React.FC = memo(() => {
    const { openFile, selectFile, selectProject, toggleProjectExpansion } = useIDEState();
    const projects = useIDESelectors.useProjects();
    const selectedFile = useIDESelectors.useSelectedFile();
    const selectedProject = useIDESelectors.useSelectedProject();

    const handleToggleExpansion = useCallback(
        (projectId: string) => () => {
            toggleProjectExpansion(projectId);
        },
        [toggleProjectExpansion]
    );

    const handleProjectSelect = useCallback(
        (projectId: string) => () => {
            selectProject(projectId);
        },
        [selectProject]
    );

    const handleFileSelect = useCallback(
        (projectId: string) => (fileId: string) => {
            selectFile(fileId, projectId);
        },
        [selectFile]
    );

    const handleFileOpen = useCallback(
        (projectId: string) => (fileId: string) => {
            openFile(fileId, projectId);
        },
        [openFile]
    );

    return (
        <div id='file-explorer' className='h-full flex flex-col transition-all duration-200'>
            <div id='file-explorer-header' className='p-2 border-b border-gray-600/30 shadow-sm'>
                <div className='text-vscode-text-secondary text-xs font-medium select-none'>EXPLORER</div>
            </div>
            <div
                id='file-explorer-content'
                role='navigation'
                aria-label='Projects and files'
                className='flex-1 overflow-auto'
                onKeyDown={event => {
                    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
                    const rows = Array.from(
                        event.currentTarget.querySelectorAll<HTMLButtonElement>('[data-explorer-item]')
                    );
                    const index = rows.indexOf(event.target as HTMLButtonElement);
                    if (index < 0) return;
                    event.preventDefault();
                    const next =
                        event.key === 'Home'
                            ? 0
                            : event.key === 'End'
                              ? rows.length - 1
                              : Math.max(0, Math.min(rows.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
                    rows[next]?.focus();
                }}
            >
                <div className='p-2 select-none'>
                    {projects.map(project => (
                        <div key={project.id} className='project-container'>
                            <ProjectItem
                                project={project}
                                selectedFile={selectedFile}
                                selectedProject={selectedProject}
                                onToggleExpansion={handleToggleExpansion(project.id)}
                                onProjectSelect={handleProjectSelect(project.id)}
                                onFileSelect={handleFileSelect(project.id)}
                                onFileOpen={handleFileOpen(project.id)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
});
